import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";

import { COMPLIANCE_API_URLS, ComplianceAPI } from "../../../../api/compliance";
import { useCustomerProvider } from "../../../../context";
import { customerActions } from "../../../../reducers/customer";
import usePagination from "../../../../hooks/usePagination";
import { CollapseWithControls, PaginationWithLimit } from "../../../../components/ui";

const ComplianceNotes = (): React.ReactElement => {
  const dispatch = useDispatch();
  const pagination = usePagination(4);

  const { customer } = useCustomerProvider();
  const { data } = useSelector((state) => state.customer.complianceNotes);

  const { isError, error } = useQuery({
    enabled: !!customer.email,
    retry: false,
    queryKey: [COMPLIANCE_API_URLS.getNotes, customer.email, pagination.limit, pagination.page],
    async queryFn({ signal }) {
      dispatch(customerActions.fetchComplianceNotesRequest());
      const query = new URLSearchParams();
      query.set("email", customer.email);
      query.set("limit", pagination.limit.toString());
      query.set("page", pagination.page.toString());

      const response = await ComplianceAPI.getCustomerComplianceNotesRequest(
        query.toString(),
        signal
      );
      pagination.setCount(response.data.count);
      dispatch(customerActions.fetchComplianceNotesSuccess(response.data));
      return response;
    },
  });

  useEffect(() => {
    if (isError && error) {
      dispatch(customerActions.fetchComplianceNotesFailed());
      console.error("Error handle saveNote", error);
    }
  }, [isError, error]);

  return (
    <Box mb="5px">
      <CollapseWithControls
        buttonText={`Compliance Notes: ${data.count}`}
        buttonProps={{
          disabled: !data.count,
          ...(data.count && { color: "secondary" }),
        }}
      >
        <Box sx={{ border: "1px solid rgba(0,0,0,.2)", borderRadius: "10px", padding: "7px" }}>
          <Box component="table" sx={{ borderCollapse: "collapse", width: "100%" }}>
            <tbody>
              {data.rows.map((r) => (
                <Box
                  component="tr"
                  key={r.id}
                  sx={
                    data.rows.length > 1
                      ? { "& td": { borderBottom: "1px solid rgba(0,0,0,.2)" } }
                      : {}
                  }
                >
                  <td>{r.measure_taken}</td>
                  <Box component="td" sx={{ minWidth: "80px", textAlign: "end" }}>
                    {r.measure_timestamp}
                  </Box>
                </Box>
              ))}
            </tbody>
          </Box>

          <PaginationWithLimit
            count={pagination.count}
            limit={pagination.limit}
            page={pagination.page}
            setPageState={pagination.setPage}
            setLimit={pagination.setLimit}
          />
        </Box>
      </CollapseWithControls>
    </Box>
  );
};

export default ComplianceNotes;
